import { useCallback, useMemo, useState } from "react";
import {
  EquipmentKind,
  MovementPattern,
  MuscleRegion,
  buildTagSeries,
  muscleName,
  taxonomy,
} from "@jackedlogs/shared";

import type { TFn } from "../i18nTypes";
import {
  useChartWorkouts,
  type UseChartWorkoutsOptions,
} from "./useChartWorkouts";

type TagSeries = ReturnType<typeof buildTagSeries>[number];
type TagLang = "en" | "it" | "fr" | "de" | "ru";

export type UseTagVolumeRecordsOptions = UseChartWorkoutsOptions & {
  t: TFn;
  // i18next's `resolvedLanguage`/`language` — used only to pick the
  // taxonomy locale for tag labels.
  resolvedLanguage: string | undefined;
};

export type UseTagVolumeRecordsResult = ReturnType<typeof useChartWorkouts> & {
  tagSeries: TagSeries[];
  visibleTagSeries: TagSeries[];
  tagFilters: string[];
  setTagFilters: (
    next: string[] | ((prev: string[]) => string[]),
  ) => void;
  localizeTag: (slug: string) => string;
};

// Volume-by-tag records — the data + localization behind
// `ProgressRecordsCard`, shared so mobile and web-rnw drive the SAME card
// from the SAME logic (lift/progress-tag-volume-web split `useChartWorkouts`
// out first; this hook is its natural sibling — tag filtering + label
// localization is host-agnostic, it only needs `t` + the resolved language).
export function useTagVolumeRecords(
  opts: UseTagVolumeRecordsOptions,
): UseTagVolumeRecordsResult {
  const { t, resolvedLanguage, ...chartOpts } = opts;
  const chart = useChartWorkouts(chartOpts);
  const [tagFilters, setTagFilters] = useState<string[]>([]);

  const tagSeries = useMemo(
    () =>
      chart.chartWorkouts === null ? [] : buildTagSeries(chart.chartWorkouts),
    [chart.chartWorkouts],
  );
  const visibleTagSeries = useMemo(
    () =>
      tagFilters.length === 0
        ? tagSeries
        : tagSeries.filter((s) => tagFilters.includes(s.tag)),
    [tagSeries, tagFilters],
  );

  // Tag label localization: muscle slug → taxonomy lookup; region/pattern/
  // equipment fall back to the shared catalog.* keys; unknown slugs raw.
  const tagLang: TagLang = useMemo(() => {
    const base = (resolvedLanguage ?? "en").split("-")[0];
    if (base === "it" || base === "fr" || base === "de" || base === "ru")
      return base;
    return "en";
  }, [resolvedLanguage]);

  const localizeTag = useCallback(
    (slug: string): string => {
      const m = taxonomy.muscles.find((mm) => mm.slug === slug);
      if (m) return muscleName(slug, tagLang);
      if (MuscleRegion.options.includes(slug as never))
        return t(`catalog.region_${slug}`, { defaultValue: slug });
      if (MovementPattern.options.includes(slug as never))
        return t(`catalog.pattern_${slug}`, { defaultValue: slug });
      if (EquipmentKind.options.includes(slug as never))
        return t(`catalog.equipment_${slug}`, { defaultValue: slug });
      return slug;
    },
    [t, tagLang],
  );

  return {
    ...chart,
    tagSeries,
    visibleTagSeries,
    tagFilters,
    setTagFilters,
    localizeTag,
  };
}
